import React, { useEffect, useState } from 'react';
import SectionTitle from '../common/SectionTitle';
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import { fetchLatestAnnouncements } from '../../redux/actions/announcementsActions';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../redux/store';
import AnnouncementFormModal from './AnnouncementFormModal';

const responsive = {
  desktop: {
    breakpoint: { max: 3000, min: 1024 },
    items: 1,
    slidesToSlide: 1,
  },
  tablet: {
    breakpoint: { max: 1024, min: 768 },
    items: 1,
    slidesToSlide: 1,
  },
  mobile: {
    breakpoint: { max: 767, min: 464 },
    items: 1,
    slidesToSlide: 1,
  },
  mobilesm: {
    breakpoint: { max: 463, min: 280 },
    items: 1,
    slidesToSlide: 1,
  },
};

const truncateText = (text: string, maxLength: number = 150) => {
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};

const LatestAnnouncements: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const announcementsData = useSelector(
    (state: any) => state.announcements || {},
  );
  const latestAnnouncements = announcementsData.latestAnnouncements || [];
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchLatestAnnouncements());
  }, [dispatch]);

  const toggleModal = () => {
    setIsModalOpen((prev) => !prev);
  };

  const renderTextWithLinks = (text: string | undefined) => {
    if (!text) return null;

    // Regular expression to detect URLs
    const urlRegex = /(https?:\/\/[^\s]+)/g;

    // Split the text by URLs
    const parts = text.split(urlRegex);

    // Find all URLs in the text
    const urls: string[] = [];
    let match;
    while ((match = urlRegex.exec(text)) !== null) {
      urls.push(match[0]);
    }

    // Combine parts and URLs
    return parts.map((part, index) => {
      // Check if this part matches a URL
      if (urls.includes(part)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            {part}
          </a>
        );
      }
      // Return regular text
      return part;
    });
  };

  return (
    <div className="mb-12">
      <SectionTitle
        title="Latest"
        titleBadge="announcement"
        buttonTitle="Announcement"
        onButtonClick={toggleModal}
      />
      {latestAnnouncements.length === 0 ? (
        <div className="flex justify-center items-center py-16 bg-gray-50 rounded-xl">
          <div className="text-center">
            <img 
              src="/assets/announcement.png" 
              alt="No announcements" 
              className="w-24 h-24 mx-auto mb-4 opacity-50"
            />
            <p className="fredoka-600 text-xl text-gray-500">
              There's no announcement at the moment
            </p>
          </div>
        </div>
      ) : (
        <div className="mt-6">
          <Carousel
            responsive={responsive}
            autoPlay={latestAnnouncements.length > 1}
            swipeable={true}
            draggable={true}
            infinite={latestAnnouncements.length > 1}
            centerMode={latestAnnouncements.length > 1}
            arrows={latestAnnouncements.length > 1}
            customTransition="transform 500ms ease-in-out"
            containerClass="carousel-container"
            itemClass="carousel-item-padding"
          >
            {latestAnnouncements
              .slice(0, Math.min(3, latestAnnouncements.length))
              .map((announcement: any) => (
                <div
                  key={announcement.id}
                  className="bg-primary-600 rounded-2xl shadow-lg overflow-hidden mx-2 my-4 relative"
                >
                  <div className="p-6 md:p-8 flex flex-col md:flex-row gap-6 md:gap-8 relative z-10">
                    {/* Image container */}
                    <div className="bg-white rounded-full w-28 h-28 md:w-36 md:h-36 lg:w-40 lg:h-40 flex-shrink-0 flex items-center justify-center shadow-md mx-auto md:mx-0 relative">
                      <img
                        className="absolute -top-2 -left-2 w-8 md:w-10"
                        src="/assets/icons/love.png"
                        alt="decoration"
                        draggable="false"
                      />
                      <img
                        src={`https://inidevloh.coppamagz.com/${announcement.image}`}
                        alt={announcement.title}
                        className={`w-3/4 h-3/4 object-cover ${announcement.image2 ? '-translate-y-1/4 -translate-x-1/4' : ''}`}
                        draggable="false"
                      />
                      {announcement.image2 && (
                        <img
                          src={`https://inidevloh.coppamagz.com/${announcement.image2}`}
                          alt={announcement.title}
                          className="w-3/4 h-3/4 object-cover translate-y-1/4 translate-x-1/4 absolute"
                          draggable="false"
                        />
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 text-left md:pr-12">
                      <h3 className="fredoka-600 text-xl md:text-2xl lg:text-3xl font-semibold text-[#FFECC7] mb-3 line-clamp-2">
                        {announcement.title || 'Default Title'}
                      </h3>
                      <p className="text-white text-sm md:text-base leading-relaxed">
                        {announcement.content
                          ? renderTextWithLinks(truncateText(announcement.content))
                          : 'Default Description'}
                      </p>
                    </div>
                  </div>
                  
                  {/* Decorative elements */}
                  <img
                    src="/assets/loves.png"
                    alt="decoration"
                    className="absolute top-0 right-0 w-24 md:w-32 lg:w-40 opacity-50 z-0"
                    draggable="false"
                  />
                  <img
                    src="/assets/girl-boy.png"
                    alt="decoration"
                    className="absolute bottom-0 right-0 w-32 md:w-40 lg:w-48 z-0"
                    draggable="false"
                  />
                </div>
              ))}
          </Carousel>
        </div>
      )}

      <AnnouncementFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};

export default LatestAnnouncements;
